export enum ScorecardStatus {
  NEEDS_SETUP = "needs_setup",
  READY = "ready",
  QUEUED = "queued",
  RUNNING = "running",
  COMPLETED = "completed",
  STALE = "stale",
  FAILED = "failed",
}

export enum ScoreVerdict {
  PASS = "pass",
  WARN = "warn",
  FAIL = "fail",
  ERROR = "error",
  SKIPPED = "skipped",
}

export enum ScorecardPrimitiveType {
  COLUMN_AGGREGATE = "COLUMN_AGGREGATE",
  CONTAINS = "CONTAINS",
  REGEX = "REGEX",
  ASSERT_VALID = "ASSERT_VALID",
  COMPARE = "COMPARE",
  LLM_ASSERTION = "LLM_ASSERTION",
  TOOL_EVALUATOR = "TOOL_EVALUATOR",
  CODE_EXECUTION = "CODE_EXECUTION",
  STRUCTURE = "STRUCTURE",
  MAX_TOOL_CALLS = "MAX_TOOL_CALLS",
  NO_INVALID_TOOL_CALLS = "NO_INVALID_TOOL_CALLS",
  COUNT = "COUNT",
  TRAJECTORY = "TRAJECTORY",
}

export interface ScorecardThresholds {
  pass: number;
  warn: number;
}

export interface ScorecardAggregationConfig {
  method: "weighted_mean";
  required_step_failure_behavior: "fail" | "warn" | "error";
  pass_threshold: number;
  warn_threshold: number;
}

export interface ScorecardStep {
  id: string;
  scorecard_id: string;
  title: string;
  description?: string | null;
  primitive_type: ScorecardPrimitiveType | string;
  target_column_id?: string | null;
  source_column_ids: string[];
  weight: number;
  required: boolean;
  thresholds: ScorecardThresholds;
  score_adapter: Record<string, unknown>;
  evidence_mode?: "summary" | "raw" | "hidden" | null;
  order_index: number;
  backing_column_id?: string | null;
  config_hash: string;
  primitive_config: Record<string, unknown>;
}

export interface ScorecardStaleState {
  is_stale: boolean;
  reasons: string[];
  stale_step_ids?: string[];
  stale_row_indices?: number[];
}

export interface Scorecard {
  id: string;
  workspace_id: number;
  smart_table_id: string;
  sheet_id: string;
  name: string;
  evaluated_column_ids: string[];
  aggregation: ScorecardAggregationConfig;
  display_config?: Record<string, unknown> | null;
  baseline_config?: Record<string, unknown> | null;
  status: ScorecardStatus | string;
  stale_state?: ScorecardStaleState | null;
  latest_calculation_id?: string | null;
  config_hash: string;
  steps: ScorecardStep[];
  created_at?: string;
  updated_at?: string;
}

export interface EvaluatorResult {
  score: number | null;
  verdict: ScoreVerdict | string;
  evidence?: string;
  raw_value: unknown;
  aggregate_result?: Record<string, unknown>;
  threshold_result?: string;
  baseline_score?: number;
  baseline_delta?: number;
  previous_score?: number;
  drift?: number;
  drift_comparable?: boolean;
  drift_unavailable_reason?: string;
  computed_at?: string;
  config_hash?: string;
  input_hash?: string;
  error_message?: string;
}

export interface ScorecardDriftSummary {
  previous_score?: number;
  drift?: number;
  drift_comparable?: boolean;
  drift_unavailable_reason?: string;
}

export interface ScorecardAssertionSummary {
  assertion: string;
  pass_count: number;
  fail_count: number;
  total_count: number;
}

export interface CriterionSummary {
  step_id: string;
  title: string;
  score: number | null;
  aggregate_result?: Record<string, unknown>;
  verdict: ScoreVerdict | string;
  completed_row_count: number;
  pass_row_count?: number;
  warn_row_count?: number;
  fail_row_count?: number;
  skipped_row_count: number;
  error_row_count: number;
  assertion_summaries?: ScorecardAssertionSummary[];
  previous_score?: number;
  drift?: number;
  drift_comparable?: boolean;
  drift_unavailable_reason?: string;
}

export interface ScorecardCalculation {
  id: string;
  scorecard_id: string;
  workspace_id: number;
  sheet_id: string;
  sheet_revision: string;
  status: string;
  aggregate_score: number | null;
  aggregate_verdict: ScoreVerdict | string | null;
  criterion_summaries: Record<string, CriterionSummary>;
  drift_summary?: ScorecardDriftSummary | null;
  config_hash: string;
  started_at?: string;
  completed_at?: string | null;
  error_summary?: string | null;
}

export interface ScorecardRowSummary {
  row_index: number;
  aggregate_score: number | null;
  aggregate_verdict: ScoreVerdict | string;
  drift_summary?: ScorecardDriftSummary | null;
  stale_state?: ScorecardStaleState | null;
  error_summary?: string | null;
}

export interface GetScorecardRowResponse {
  success: true;
  row_index: number;
  calculation_id: string;
  aggregate_score: number | null;
  aggregate_verdict: ScoreVerdict | string;
  step_results: Record<string, EvaluatorResult>;
  drift_summary?: ScorecardDriftSummary | null;
  stale_state?: ScorecardStaleState | null;
  error_summary?: string | null;
}

export interface GetScorecardResponse {
  success: true;
  scorecard: Scorecard | null;
  latest_calculation: ScorecardCalculation | null;
  display_calculation?: ScorecardCalculation | null;
  criterion_summaries?: Record<string, CriterionSummary>;
  drift_summary?: ScorecardDriftSummary | null;
  progress?: {
    scored_rows: number;
    total_rows: number;
    partial_score: number | null;
    passed_rows?: number;
    failed_rows?: number;
    warned_rows?: number;
    running_rows?: number;
    queued_rows?: number;
  } | null;
}

export interface ConfigureScorecardStepRequest {
  id?: string;
  title: string;
  description?: string | null;
  primitive_type: ScorecardPrimitiveType | string;
  target_column_id?: string | null;
  source_column_ids?: string[];
  weight?: number;
  required?: boolean;
  thresholds?: ScorecardThresholds;
  score_adapter?: Record<string, unknown>;
  evidence_mode?: "summary" | "raw" | "hidden" | null;
  order_index?: number;
  primitive_config: Record<string, unknown>;
}

export interface ConfigureScorecardRequest {
  name: string;
  evaluated_column_ids: string[];
  steps: ConfigureScorecardStepRequest[];
  aggregation: ScorecardAggregationConfig;
  display_config?: Record<string, unknown> | null;
  baseline_config?: Record<string, unknown> | null;
}

export interface ConfigureScorecardResponse {
  success: true;
  scorecard: Scorecard;
  version: number;
}

export interface LegacyScorecardConversionSkippedItem {
  column_id?: string;
  column_title?: string;
  score_type?: string;
  reason: string;
}

export interface MigrateLegacyScorecardResponse {
  success: true;
  scorecard: Scorecard | null;
  converted_count: number;
  skipped: LegacyScorecardConversionSkippedItem[];
  legacy_score_deleted: boolean;
  version: number;
}

export interface RecalculateScorecardRequest {
  row_indices?: number[];
  step_ids?: string[];
}

export type CancelScorecardRequest = RecalculateScorecardRequest;

export interface RecalculateScorecardResponse {
  success: true;
  calculation_id: string;
  status: ScorecardStatus | string;
  version: number;
}

export interface CancelScorecardResponse {
  success: true;
  message: string;
  scorecard: Scorecard;
  cancelled_count: number;
  execution_ids: string[];
  calculation_id: string | null;
}

export const SCORECARD_DEFAULT_THRESHOLDS: ScorecardThresholds = {
  pass: 0.8,
  warn: 0.6,
};

export const SCORECARD_DEFAULT_AGGREGATION: ScorecardAggregationConfig = {
  method: "weighted_mean",
  required_step_failure_behavior: "fail",
  pass_threshold: SCORECARD_DEFAULT_THRESHOLDS.pass,
  warn_threshold: SCORECARD_DEFAULT_THRESHOLDS.warn,
};

export const SCORECARD_VERDICT_LABELS: Record<string, string> = {
  pass: "Pass",
  warn: "Review",
  fail: "Fail",
  error: "Error",
  skipped: "Skipped",
};

export const SCORECARD_PRIMITIVE_LABELS: Record<string, string> = {
  COLUMN_AGGREGATE: "Evaluate Column",
  LLM_ASSERTION: "AI Judge",
  COMPARE: "Exact Match",
  CONTAINS: "Contains Text",
  REGEX: "Regex Match",
  ASSERT_VALID: "Valid Format",
  TOOL_EVALUATOR: "Tool Evaluator",
  CODE_EXECUTION: "Code Check",
  STRUCTURE: "Structure Check",
  MAX_TOOL_CALLS: "Max Tool Calls",
  NO_INVALID_TOOL_CALLS: "No Invalid Tool Calls",
  COUNT: "Count Check",
  TRAJECTORY: "Trajectory Check",
};
